import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../context/CurrencyContext";
import ClientShell from "../client-portal/ClientShell";
import { isImpersonating, exitImpersonation } from "../../lib/impersonation";
import api, { notificationsApi, currencyApi } from "../../lib/api";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Badge } from "../ui/badge";
import BrandLogo from "../BrandLogo";
import ThemeToggle from "./ThemeToggle";
import { toast } from "sonner";
import {
  LayoutDashboard,
  FolderKanban,
  ListTodo,
  Users,
  Images,
  Palette,
  UserCircle,
  Banknote,
  CreditCard,
  Wallet,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  TrendingUp,
  RefreshCw,
  Target,
  Settings,
  Download,
  Edit3,
  Receipt,
  Building2,
  DollarSign,
  Calculator,
  FileText,
  FileSignature,
  Send,
  Calendar,
  AlertTriangle,
  Timer,
  Eye,
  BarChart3,
  Award,
  UserPlus,
  MessageCircle,
  ClipboardList,
  Shield,
  Box,
  Tag,
  Brain,
  Database,
  Bug,
  Flame,
  BookOpen,
  Inbox,
  Megaphone,
  Globe2,
  Percent,
  LifeBuoy,
  CalendarDays,
  Clock,
  Network,
  GraduationCap,
  Phone,
  ListFilter, MessagesSquare, Map as MapIcon } from "lucide-react";

const ROADMAP_EMAILS = ["alexander@smartorange.com", "kuzmenko@smarto.agency", "dmytro.medvedev@smarto.agency", "dron_2019@ukr.net"];

const SidebarLayout = ({ children, roleOverride, nameOverride }) => {
  const { user, logout } = useAuth();
  // "View as client" preview renders this layout with the CLIENT's role so the
  // sidebar/nav is identical to what the client sees, while the real session
  // stays the internal user. effectiveRole drives every role-based decision.
  const effectiveRole = roleOverride || user?.role;
  const effectiveName = nameOverride || user?.name;
  const { displayCurrency, cycleCurrency, getRateDisplay, getCurrencySymbol, rates, fetchExchangeRates, rateDate } = useCurrency();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [errorBadge, setErrorBadge] = useState(0);
  // Marketing-analytics access — both role-based AND email-allowlist-based.
  // Lazily fetched once per mount; result decides whether the sidebar entry
  // shows for users not in MARKETING_READ_ROLES (e.g. external marketing
  // agency staff added via integration_settings.marketing_analytics_allowed_emails).
  const [marketingAccess, setMarketingAccess] = useState(null);
  
  // Submenu states - Plan B structure
  const [mainOpen, setMainOpen] = useState(true);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [crmOpen, setCrmOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);
  const [expensesOpen, setExpensesOpen] = useState(false);
  const [incomeOpen, setIncomeOpen] = useState(false);
  const [managementOpen, setManagementOpen] = useState(false);
  // Opens itself when you are already inside it.
  const [portfolioOpen, setPortfolioOpen] = useState(() => typeof window !== "undefined" && window.location.pathname.startsWith("/portfolio"));
  const [adminOpen, setAdminOpen] = useState(false);
  
  // Currency settings modal
  const [currencyModalOpen, setCurrencyModalOpen] = useState(false);
  const [manualUsd, setManualUsd] = useState("");
  const [manualEur, setManualEur] = useState("");
  const [fetchingRates, setFetchingRates] = useState(false);
  const [myTasks, setMyTasks] = useState(null);
  const [savingRates, setSavingRates] = useState(false);

  useEffect(() => {
    // In "view as client" preview, skip notification/error fetches — those are
    // the internal user's data and would leak the manager's "46" badge etc.
    if (roleOverride) return;
    fetchNotifications();
    if (effectiveRole === "admin") {
      fetchErrorBadge();
      const interval = setInterval(fetchErrorBadge, 60_000);
      return () => clearInterval(interval);
    }
  }, [effectiveRole, roleOverride]);

  // Does this person have tasks assigned to them as an executor? Role does not
  // tell us — an art director (or anyone else) may also take tasks, and until
  // now they had no list of their own work. Data-driven, so nobody gets a dead
  // menu entry. backlog 833d248d.
  useEffect(() => {
    if (!user || roleOverride || effectiveRole === "artist") return;
    let cancelled = false;
    api.get("/me/assigned-tasks")
      .then((r) => { if (!cancelled) setMyTasks(r.data); })
      .catch(() => { if (!cancelled) setMyTasks(null); });
    return () => { cancelled = true; };
  }, [user, roleOverride, effectiveRole]);

  // One-shot fetch of marketing analytics access — covers email-allowlist users
  // whose role isn't in the static MARKETING_READ_ROLES list. Failure = no entry.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/marketing-analytics/access")
      .then((r) => { if (!cancelled) setMarketingAccess(r.data); })
      .catch(() => { if (!cancelled) setMarketingAccess({ can_view: false }); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      const unread = response.data.filter((n) => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchErrorBadge = async () => {
    try {
      const { default: api } = await import("../../lib/api");
      const { data } = await api.get("/errors/stats");
      setErrorBadge(data?.new || 0);
    } catch (e) {
      // silent — endpoint is admin-only and may 401/403 during boot
    }
  };
  
  const openCurrencyModal = () => {
    setManualUsd(rates?.USD?.toFixed(4) || "41.5");
    setManualEur(rates?.EUR?.toFixed(4) || "43.5");
    setCurrencyModalOpen(true);
  };
  
  const handleFetchRates = async () => {
    try {
      setFetchingRates(true);
      const response = await currencyApi.fetchToday();
      if (response.data) {
        setManualUsd(response.data.usd_to_uah?.toFixed(4) || "41.5");
        setManualEur(response.data.eur_to_uah?.toFixed(4) || "43.5");
        toast.success("Rates fetched from NBU");
        fetchExchangeRates();
      }
    } catch (error) {
      toast.error("Failed to fetch rates");
    } finally {
      setFetchingRates(false);
    }
  };
  
  const handleSaveRates = async () => {
    try {
      setSavingRates(true);
      await currencyApi.setRate({
        usd_to_uah: parseFloat(manualUsd),
        eur_to_uah: parseFloat(manualEur),
      });
      toast.success("Rates saved");
      fetchExchangeRates();
      setCurrencyModalOpen(false);
    } catch (error) {
      toast.error("Failed to save rates");
    } finally {
      setSavingRates(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getNavItems = () => {
    // Role-specific standalone items (artist, client, partner)
    const items = [
      // Artist role items
      { path: "/artist-dashboard", label: "My Dashboard", icon: LayoutDashboard, roles: ["artist"] },
      { path: "/artist-tasks", label: "My Tasks", icon: ListTodo, roles: ["artist"] },
      { path: "/artist-contract", label: "Contract", icon: FileText, roles: ["artist"] },
      { path: "/profile", label: "Profile", icon: UserCircle, roles: ["artist"] },
      // Client role items
      { path: "/client", label: "Кабінет", icon: LayoutDashboard, roles: ["client"] },
      { path: "/client/projects", label: "Мої проєкти", icon: FolderKanban, roles: ["client"] },
      { path: "/client/requests", label: "Від вас", icon: Send, roles: ["client"] },
      { path: "/client/payments", label: "Рахунки до сплати", icon: Wallet, roles: ["client"] },
      // Partner role items
      { path: "/trp/sales", label: "TRP Projects", icon: Building2, roles: ["partner"] },
      { path: "/trp/tasks", label: "Tasks", icon: ListTodo, roles: ["partner"] },
      { path: "/trp/artists", label: "Artists", icon: Palette, roles: ["partner"] },
      { path: "/trp/requests", label: "Requests", icon: DollarSign, roles: ["partner"] },
      { path: "/trp/payments", label: "Payments due", icon: Wallet, roles: ["partner"] },
      { path: "/my-feedback", label: "My feedback", icon: MessageCircle, roles: ["partner"] },
    ];

    const filtered = items.filter((item) => item.roles.includes(effectiveRole));

    // Standalone Roleplay entry for trainer-only access: the isolated
    // role=roleplay_tester, OR any other role granted the additive
    // roleplay_access flag (e.g. a partner). Sales-team roles already get
    // Roleplay inside the CRM section, so skip them here to avoid a duplicate.
    // Suppressed in "view as client" preview (roleOverride). Marker: roleplay-access-flag-v1
    const CRM_ROLEPLAY_ROLES = ["admin", "sales", "sales_director", "crm_manager", "pm_lead"];
    const showRoleplay =
      !roleOverride &&
      (effectiveRole === "roleplay_tester" ||
        (!!user?.roleplay_access && !CRM_ROLEPLAY_ROLES.includes(effectiveRole)));
    if (showRoleplay) {
      filtered.push({ path: "/roleplay", label: "Roleplay Trainer", icon: GraduationCap });
    }
    return filtered;
  };

  // PLAN B: Main section items
  const getMainItems = () => {
    const items = [
      { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["admin", "pm_lead", "pm", "sales_director", "crm_manager", "art_director", "sales", "finance"] },
      { path: "/profile", label: "Profile", icon: UserCircle, roles: ["admin", "team", "pm", "sales", "pm_lead", "sales_director", "crm_manager", "art_director", "finance", "artist"] },
      // Who's off and when — company-wide, so it lives in Main next to Profile
      // rather than under Management where only managers ever found it.
      // backlog 46bb95e0
      { path: "/team-calendar", label: "Time Off Calendar", icon: CalendarDays, roles: ["admin", "team", "pm", "sales", "pm_lead", "sales_director", "crm_manager", "art_director", "finance"] },
    ];
    // SpinHouse roadmap — shown by email, not role (mirror of ROADMAP_EMAILS
    // in server.py, which is the real gate).
    if (ROADMAP_EMAILS.includes(String(user?.email || "").toLowerCase())) {
      items.splice(1, 0, { path: "/roadmap", label: "Roadmap", icon: MapIcon, roles: [effectiveRole] });
    }
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // PLAN B: Projects section items
  const getProjectItems = () => {
    const items = [
      { path: "/unified-projects", label: "All Projects", icon: FolderKanban, roles: ["admin", "pm_lead", "pm", "sales", "art_director", "crm_manager", "sales_director"] },
      { path: "/projects", label: "PM Projects", icon: FolderKanban, roles: ["admin", "pm_lead", "art_director", "crm_manager"] },
      { path: "/tasks", label: "Tasks", icon: ListTodo, roles: ["admin", "pm_lead", "pm", "art_director", "crm_manager"] },
      // Shown to whoever actually has tasks on them (see the effect above),
      // not by role — that is the whole point.
      ...(myTasks?.total ? [{ path: "/my-tasks", label: "My Tasks", icon: ListTodo, roles: [effectiveRole] }] : []),
      { path: "/art-director/review", label: "Review Queue", icon: Eye, roles: ["admin", "art_director", "pm", "pm_lead"] },
      { path: "/client-requests", label: "Client Requests", icon: Send, roles: ["admin", "pm_lead", "pm", "art_director", "crm_manager"] },
      { path: "/waiting-management", label: "Waiting Management", icon: Timer, roles: ["admin", "pm_lead", "pm", "crm_manager"] },
    ];
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // PLAN B: Finance section items
  const getFinanceItems = () => {
    const items = [
      { path: "/cashflow", label: "Cashflow Forecast", icon: Flame, roles: ["admin", "sales_director", "pm_lead", "crm_manager", "finance"] },
      { path: "/partner-calculator", label: "Partner Calculator", icon: Calculator, roles: ["admin", "sales_director", "pm_lead", "finance"] },
      { path: "/finance/documents", label: "Invoices & Acts", icon: FileText, roles: ["admin", "finance", "pm_lead", "sales_director"] },
      { path: "/bank-transactions", label: "Bank Transactions", icon: Building2, roles: ["admin", "sales_director", "pm_lead", "finance"] },
      { path: "/my-payments", label: "My Payments", icon: Banknote, roles: ["admin", "team", "pm", "sales", "pm_lead", "sales_director", "crm_manager", "art_director", "finance"] },
      { path: "/pm-bonuses", label: "PM Bonuses", icon: Award, roles: ["admin", "pm_lead", "pm"] },
      { path: "/my-expenses", label: "My Expenses", icon: Receipt, roles: ["admin", "team", "pm", "sales", "pm_lead", "sales_director", "crm_manager", "art_director", "finance"] },
      { path: "/general-pnl", label: "General P&L", icon: Calculator, roles: ["admin"] },
      { path: "/sales-pnl", label: "Sales P&L", icon: TrendingUp, roles: ["admin", "sales_director"] },
      { path: "/crm-pnl", label: "CRM P&L", icon: DollarSign, roles: ["admin", "crm_manager"] },
      { path: "/pnl-forecast", label: "Staff Forecast", icon: TrendingUp, roles: ["admin"] },
      { path: "/staff-pnl", label: "Staff P&L Detail", icon: Users, roles: ["admin", "finance"] },
      { path: "/artist-pnl", label: "Artist P&L", icon: Palette, roles: ["admin", "pm_lead", "finance"] },
    ];
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // PLAN B: Expenses section items
  const getExpensesItems = () => {
    const items = [
      { path: "/payments", label: "Artist Payments", icon: Palette, roles: ["admin", "finance", "pm_lead", "pm"] },
      { path: "/team-expenses", label: "Team Salaries", icon: Users, roles: ["admin", "finance", "pm_lead"] },
      { path: "/other-expenses", label: "Other Expenses", icon: Building2, roles: ["admin", "finance", "pm_lead"] },
    ];
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // PLAN B: Income section items
  const getIncomeItems = () => {
    const items = [
      { path: "/income", label: "All Income", icon: Wallet, roles: ["admin", "crm_manager", "finance", "pm_lead"] },
      { path: "/sales", label: "Sales Projects", icon: Target, roles: ["admin", "sales", "sales_director", "pm_lead"] },
      { path: "/crm-income", label: "CRM Payments", icon: DollarSign, roles: ["admin", "crm_manager", "pm_lead"] },
      { path: "/support-payments", label: "Support Payments", icon: DollarSign, roles: ["admin", "crm_manager", "pm_lead"] },
      { path: "/trp/sales", label: "TRP Sales", icon: Building2, roles: ["admin", "pm_lead"] },
    ];
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // Sales & marketing section items.
  // The deal/contact/company lists that used to live here are gone: sales is
  // owned by the partner CRM (crm.smartorange.space) and HUB no longer draws a
  // second copy of it. What stays are the JOINS the CRM cannot make — ad spend
  // × lead quality × deal × project × margin. Marker: sales-crm-handoff-v1
  const getCrmItems = () => {
    // NB: "sales" (sales managers) intentionally excluded from these analytics —
    // per confidentiality request they see ONLY "Roleplay Trainer" in this
    // section (its own roles array below keeps sales). sales_director keeps
    // full access. Marker: sales-crm-confidential-v1
    const roles = ["admin", "sales_director", "crm_manager", "pm_lead"];
    const items = [
      { path: "/roleplay", label: "Roleplay Trainer", icon: GraduationCap, roles: ["admin", "sales", "sales_director", "crm_manager", "pm_lead"] },
      { path: "/call-reviews", label: "Call Reviews", icon: Phone, roles: ["admin", "sales_director", "crm_manager", "pm_lead"] },
      // Pipeline mirror: the CRM owns the records, but the whole sales picture
      // (leads, stages, per-manager, per-campaign) is readable here without
      // opening the partner subdomain. Marker: sales-leads-board-v1
      { path: "/sales-leads", label: "Лиды и воронка", icon: ListFilter, roles: ["admin", "sales_director", "crm_manager", "pm_lead"] },
      // Proposals (КП) built in the partner CRM's calculator — read-only analytics.
      { path: "/sales-proposals", label: "Коммерческие предложения", icon: FileText, roles: ["admin", "sales_director", "crm_manager", "pm_lead"] },
      { path: "/social-leads", label: "Messenger leads", icon: MessagesSquare, roles: ["admin", "sales_director", "crm_manager", "pm_lead"] },
      // Own personal-Telegram client: each person sees only the accounts assigned
      // to them, so sales may have it (it is their chats, not team analytics).
      { path: "/telegram", label: "Telegram", icon: MessagesSquare, roles: ["admin", "sales", "sales_director", "crm_manager", "pm_lead", "pm"] },
      // Marketing Analytics entry visible to internal team + marketing-agency
      // allowlist (extraAccess). Sub-pages (People / Landings) are hidden
      // from agency users — they get scope="agency" from the access endpoint
      // and the page-level gates 403 them anyway. Marker: marketing-analytics-scope-v1
      { path: "/marketing-analytics", label: "Marketing Analytics", icon: Megaphone, roles: ["admin", "sales_director", "crm_manager", "pm_lead"], extraAccess: marketingAccess?.can_view },
      { path: "/marketing-analytics/people", label: "People", icon: Users, roles: ["admin", "sales_director", "crm_manager", "pm_lead"], extraAccess: marketingAccess?.scope === "full" },
      { path: "/marketing-analytics/landings", label: "Landings & Leads", icon: Globe2, roles: ["admin", "sales_director", "crm_manager", "pm_lead"], extraAccess: marketingAccess?.scope === "full" },
      { path: "/marketing-analytics/chats", label: "Chats", icon: MessagesSquare, roles: ["admin", "sales_director", "crm_manager", "pm_lead"], extraAccess: marketingAccess?.scope === "full" },
      // Ads Quality: spend ⨝ lead quality (project size + company). Internal only —
      // no extraAccess, external marketing agencies must NOT see lead company names.
      { path: "/ads-quality", label: "Ads Quality", icon: Target, roles: ["admin", "sales_director", "crm_manager", "pm_lead"] },
    ];
    // In "view as client" preview, suppress extraAccess — it reflects the REAL
    // (internal) user's allowlist (e.g. marketing-analytics), which must NOT leak
    // into the mirrored client sidebar.
    // sales managers are confined to Roleplay Trainer in this section, so the
    // extraAccess override (marketing-analytics allowlist) must NOT resurrect
    // Marketing Analytics for them. Other roles keep the allowlist behaviour.
    return items.filter(
      (item) =>
        item.roles.includes(effectiveRole) ||
        (item.extraAccess === true && !roleOverride && effectiveRole !== "sales"),
    );
  };

  // Portfolio section: 3D twins are the product, so each brand gets its own
  // entry you land on directly. "All projects" keeps the cross-brand view with
  // filters, and studio work (sites, renders) sits below the twins, not among
  // them.
  const getPortfolioItems = () => {
    const roles = ["admin", "pm_lead", "pm", "art_director", "sales", "sales_director", "crm_manager"];
    const items = [
      { path: "/portfolio", label: "All projects", icon: LayoutDashboard, roles },
      { path: "/portfolio/vexos", label: "VEXOS", icon: Images, roles },
      { path: "/portfolio/smartorange", label: "SmartOrange", icon: Images, roles },
      { path: "/portfolio/studio", label: "Studio work", icon: Palette, roles },
    ];
    // portfolio_access: one person opened on top of their role (not in
    // "view as client" preview, where the flag is the real user's).
    if (user?.portfolio_access && !roleOverride) return items;
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // PLAN B: Management section items
  const getManagementItems = () => {
    const items = [
      { path: "/users", label: "Users", icon: Users, roles: ["admin", "pm_lead", "finance"] },
      { path: "/staff", label: "Staff", icon: UserCircle, roles: ["admin", "finance", "pm_lead"] },
      { path: "/org-structure", label: "Org Structure", icon: Network, roles: ["admin", "finance", "pm_lead"] },
      { path: "/team-utilization", label: "Team Utilization", icon: Clock, roles: ["admin", "finance", "pm_lead"] },
      { path: "/staff/bonus-matrix", label: "Bonus Matrix", icon: Percent, roles: ["admin", "finance", "pm_lead"] },
      { path: "/clients", label: "Clients", icon: Building2, roles: ["admin", "pm_lead", "pm", "sales", "sales_director", "crm_manager"] },
      { path: "/crm-dashboard", label: "CRM Clients", icon: Building2, roles: ["admin", "crm_manager"] },
      { path: "/artists", label: "Artists", icon: Palette, roles: ["admin", "pm_lead", "pm", "art_director", "crm_manager"] },
      { path: "/candidates", label: "Candidates", icon: UserPlus, roles: ["admin", "pm_lead", "pm", "art_director", "team", "sales_director"] },
      { path: "/service-rates", label: "Service Rates", icon: BarChart3, roles: ["admin", "pm_lead", "art_director", "finance"] },
    ];
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  // PLAN B: Admin section items, grouped by purpose so the 19-item flat
  // list isn't a wall. Each item carries a `group` key; render code
  // injects a small header before the first row of each group. Order of
  // groups defines display order.
  const ADMIN_GROUP_ORDER = ["workflow", "finance", "support", "integrations", "system"];
  const ADMIN_GROUP_LABELS = {
    workflow: "Workflow",
    finance: "Finance & billing",
    support: "Support hub",
    integrations: "Integrations & API",
    system: "System & logs",
  };

  const getAdminItems = () => {
    const items = [
      // Workflow — how work flows through the portal
      { group: "workflow", path: "/settings/services", label: "Services", icon: Settings, roles: ["admin", "team", "pm_lead", "sales_director", "art_director", "crm_manager"] },
      { group: "workflow", path: "/settings/timelines", label: "Timelines", icon: Calendar, roles: ["admin", "pm_lead", "art_director", "crm_manager"] },
      { group: "workflow", path: "/settings/statuses", label: "Project Statuses", icon: ListTodo, roles: ["admin", "pm_lead"] },
      { group: "workflow", path: "/settings/setup-flows", label: "Setup Chat Flows", icon: MessageCircle, roles: ["admin", "sales_director", "pm_lead"] },
      { group: "workflow", path: "/settings/ai-qc", label: "AI Quality Control", icon: Brain, roles: ["admin", "art_director"] },

      // Finance & billing — money config
      { group: "finance", path: "/settings/fops", label: "FOP Settings", icon: CreditCard, roles: ["admin"] },
      { group: "finance", path: "/settings/support-tariffs", label: "Support Tariffs", icon: LifeBuoy, roles: ["admin", "crm_manager", "finance", "pm_lead"] },
      { group: "finance", path: "/settings/payment-penalty", label: "Late-payment Penalty", icon: Flame, roles: ["admin", "sales_director", "pm_lead", "crm_manager", "finance"] },
      { group: "finance", path: "/settings/loaded-cost", label: "Fully-loaded Margin", icon: Calculator, roles: ["admin", "sales_director", "pm_lead", "finance"] },
      { group: "finance", path: "/settings/invoice-description", label: "Invoice Wording", icon: FileSignature, roles: ["admin", "finance", "sales_director", "pm_lead", "crm_manager"] },

      // Support hub — customer-facing chat infrastructure
      { group: "support", path: "/settings/support-chats", label: "Support Chats", icon: MessageCircle, roles: ["admin", "pm_lead", "pm", "developer"] },
      { group: "support", path: "/settings/support-kb", label: "Support Knowledge Base", icon: BookOpen, roles: ["admin"] },
      { group: "support", path: "/settings/support-products", label: "Support Products", icon: Box, roles: ["admin"] },
      { group: "support", path: "/settings/support-access", label: "Support Team Access", icon: Shield, roles: ["admin"] },

      // Integrations & API — external systems
      { group: "integrations", path: "/settings/integrations", label: "Integrations", icon: CreditCard, roles: ["admin"] },
      { group: "integrations", path: "/settings/wepster", label: "Wepster", icon: MessagesSquare, roles: ["admin", "sales_director"] },
      { group: "integrations", path: "/settings/telegram", label: "Telegram (own)", icon: MessagesSquare, roles: ["admin"] },
      { group: "integrations", path: "/settings/worksection/users", label: "Worksection users", icon: Clock, roles: ["admin", "pm_lead"] },
      { group: "integrations", path: "/sandbox", label: "Sandbox API", icon: Box, roles: ["admin"] },

      // System & logs — meta/internal tools
      { group: "system", path: "/settings/errors", label: "Error Logs", icon: Bug, roles: ["admin"], badgeKey: "errors" },
      { group: "system", path: "/dev-backlog", label: "Dev Backlog", icon: ClipboardList, roles: ["admin", "pm_lead", "pm", "developer"] },
      { group: "system", path: "/changelog", label: "Changelog", icon: Tag, roles: ["admin"] },
      { group: "system", path: "/settings/notifications", label: "Notifications", icon: Bell, roles: ["admin", "pm_lead", "pm", "team", "artist", "sales", "crm_manager", "art_director", "partner"] },
      { group: "system", path: "/settings/orphaned-projects", label: "Orphaned Projects", icon: AlertTriangle, roles: ["admin"] },
      { group: "system", path: "/admin/security", label: "Security & Backup", icon: Shield, roles: ["admin"] },
    ];
    return items.filter((item) => item.roles.includes(effectiveRole));
  };

  const navItems = getNavItems();
  const mainItems = getMainItems();
  const projectItems = getProjectItems();
  const crmItems = getCrmItems();
  const financeItems = getFinanceItems();
  const expensesItems = getExpensesItems();
  const incomeItems = getIncomeItems();
  const portfolioItems = getPortfolioItems();
  const managementItems = getManagementItems();
  const adminItems = getAdminItems();
  const showMainSection = mainItems.length > 0;
  const showProjectsSection = projectItems.length > 0;
  const showCrmSection = crmItems.length > 0;
  const showFinanceSection = financeItems.length > 0;
  const showExpensesSection = expensesItems.length > 0;
  const showIncomeSection = incomeItems.length > 0;
  const showPortfolioSection = portfolioItems.length > 0;
  const showManagementSection = managementItems.length > 0;
  const showAdminSection = adminItems.length > 0;

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "admin": return "bg-red-500/10 text-red-400 border-red-500/20";
      case "pm_lead": return "bg-orange-500/10 text-orange-400 border-orange-500/20";
      case "pm": return "bg-blue-500/10 text-blue-400 border-blue-500/20";
      case "team": return "bg-sky-500/10 text-sky-400 border-sky-500/20";
      case "art_director": return "bg-pink-500/10 text-pink-400 border-pink-500/20";
      case "sales": return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
      case "sales_director": return "bg-teal-500/10 text-teal-400 border-teal-500/20";
      case "artist": return "bg-purple-500/10 text-purple-400 border-purple-500/20";
      case "client": return "bg-green-500/10 text-green-400 border-green-500/20";
      case "partner": return "bg-amber-500/10 text-amber-400 border-amber-500/20";
      case "roleplay_tester": return "bg-indigo-500/10 text-indigo-400 border-indigo-500/20";
      default: return "bg-zinc-800 text-zinc-400";
    }
  };

  // Client role gets its own shell (approved cabinet prototype), not the
  // internal sidebar.
  if (effectiveRole === "client") {
    return <ClientShell name={effectiveName}>{children}</ClientShell>;
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* "View as client" read-only impersonation banner — only in the per-tab
          preview session. Makes the mode unmistakable + offers a clean exit. */}
      {isImpersonating() && (
        <div className="fixed top-0 left-0 right-0 z-[60] flex items-center justify-center gap-3 bg-amber-600 text-black text-xs font-medium px-4 py-1.5">
          <Eye className="h-3.5 w-3.5 flex-shrink-0" />
          <span className="truncate">
            Перегляд як клієнт{effectiveName ? `: ${effectiveName}` : ""} · режим тільки для перегляду (read-only)
          </span>
          <button
            type="button"
            onClick={() => {
              exitImpersonation();
              window.close();
              // If the tab can't be closed (not script-opened), fall back to login.
              setTimeout(() => { window.location.href = "/login"; }, 100);
            }}
            className="underline underline-offset-2 hover:no-underline flex-shrink-0"
          >
            Вийти
          </button>
        </div>
      )}
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-zinc-950/90 backdrop-blur-md border-b border-zinc-800 z-50 flex items-center justify-between px-4">
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
          <Menu className="h-5 w-5" />
        </Button>
        <div className="flex items-center gap-2">
          <BrandLogo className="h-6" />
          <span className="text-sm font-semibold text-zinc-50">HUB</span>
        </div>
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <Link to="/notifications" className="relative" data-testid="mobile-notifications-btn">
            <Bell className="h-5 w-5 text-zinc-400" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-orange-500 rounded-full text-xs flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </Link>
        </div>
      </div>

      {/* Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/50 z-50"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — flex column so the nav region between header and footer
          can size itself to whatever's left (no hardcoded vh math that breaks
          when items get added or zoom level changes). */}
      <aside
        className={cn(
          "fixed top-0 left-0 h-full w-64 bg-zinc-950 border-r border-zinc-800 z-50 transition-transform duration-200 flex flex-col",
          "lg:translate-x-0",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-2">
            <BrandLogo className="h-7" />
            <span className="text-sm font-semibold text-zinc-300">HUB</span>
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* User Info */}
        <div className="p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 bg-zinc-800">
              <AvatarFallback className="bg-zinc-800 text-zinc-300 font-medium">
                {effectiveName?.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-zinc-50 truncate">{effectiveName}</p>
              <Badge variant="outline" className={cn("text-xs capitalize mt-1", getRoleBadgeColor(effectiveRole))}>
                {effectiveRole}
              </Badge>
            </div>
          </div>
        </div>

        {/* Currency Switcher - available for all users */}
        <div className="px-4 py-3 border-b border-zinc-800">
          <div className="flex items-center gap-2">
            <button
              onClick={cycleCurrency}
              className="flex-1 flex items-center justify-between px-3 py-2 rounded-md bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
              data-testid="currency-switcher"
            >
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 text-zinc-500" />
                <span className="text-sm text-zinc-400">Currency</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "text-sm font-mono font-medium",
                  displayCurrency === "UAH" ? "text-green-400" : displayCurrency === "USD" ? "text-sky-400" : "text-purple-400"
                )}>
                  {getCurrencySymbol()} {displayCurrency}
                </span>
              </div>
            </button>
            {effectiveRole === "admin" && (
              <button
                onClick={openCurrencyModal}
                className="p-2 rounded-md bg-zinc-800/50 hover:bg-zinc-800 transition-colors"
                data-testid="currency-settings-btn"
                title="Currency Settings"
              >
                <Settings className="h-4 w-4 text-zinc-500 hover:text-orange-400" />
              </button>
            )}
          </div>
          <p className="text-xs text-zinc-600 mt-1 px-1">
            {getRateDisplay()}
          </p>
        </div>

        {/* Navigation — flex-1 + min-h-0 lets it claim leftover height between
            the user/currency header above and the bottom action bar below.
            min-h-0 is the standard fix for an overflow child inside a flex
            column (without it, intrinsic content height wins and overflow
            never triggers). */}
        <nav className="p-4 space-y-1 overflow-y-auto flex-1 min-h-0">
          {/* Role-specific standalone items (artist, client, partner) */}
          {navItems.map((item) => {
            const isExactMatch = location.pathname === item.path;
            const isNestedMatch = item.path !== "/dashboard" && 
              item.path !== "/client" && 
              location.pathname.startsWith(item.path + "/");
            const isActive = isExactMatch || isNestedMatch;
            const Icon = item.icon;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                  isActive
                    ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{item.label}</span>
                {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
              </Link>
            );
          })}

          {/* PLAN B: Main Section */}
          {showMainSection && (
            <div className="pt-1">
              <button
                onClick={() => setMainOpen(!mainOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  mainItems.some(i => location.pathname === i.path)
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="main-toggle"
              >
                <LayoutDashboard className="h-4 w-4" />
                <span className="font-medium">Main</span>
                {mainOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {mainOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {mainItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PLAN B: Projects Section */}
          {showProjectsSection && (
            <div className="pt-1">
              <button
                onClick={() => setProjectsOpen(!projectsOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  projectItems.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + "/"))
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="projects-toggle"
              >
                <FolderKanban className="h-4 w-4" />
                <span className="font-medium">Projects</span>
                {projectsOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {projectsOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {projectItems.map((item) => {
                    const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* CRM (HubSpot mirror) Section */}
          {showCrmSection && (
            <div className="pt-1">
              <button
                onClick={() => setCrmOpen(!crmOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  crmItems.some(i => location.pathname === i.path || location.pathname.startsWith(i.path + "/"))
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="crm-toggle"
              >
                <Database className="h-4 w-4" />
                <span className="font-medium">Sales & Marketing</span>
                {crmOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {crmOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {crmItems.map((item) => {
                    const isActive = location.pathname === item.path
                      || location.pathname.startsWith(item.path + "/");
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PLAN B: Finance Section */}
          {showFinanceSection && (
            <div className="pt-1">
              <button
                onClick={() => setFinanceOpen(!financeOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  financeItems.some(i => location.pathname === i.path)
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="finance-toggle"
              >
                <Banknote className="h-4 w-4" />
                <span className="font-medium">Finance</span>
                {financeOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {financeOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {financeItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PLAN B: Expenses Section */}
          {showExpensesSection && (
            <div className="pt-1">
              <button
                onClick={() => setExpensesOpen(!expensesOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  expensesItems.some(i => location.pathname === i.path)
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="expenses-toggle"
              >
                <Receipt className="h-4 w-4" />
                <span className="font-medium">Expenses</span>
                {expensesOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {expensesOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {expensesItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PLAN B: Income Section */}
          {showIncomeSection && (
            <div className="pt-1">
              <button
                onClick={() => setIncomeOpen(!incomeOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  incomeItems.some(i => location.pathname === i.path)
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="income-toggle"
              >
                <DollarSign className="h-4 w-4" />
                <span className="font-medium">Income</span>
                {incomeOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {incomeOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {incomeItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}


          {/* Portfolio Section — 3D twins per brand + studio work */}
          {showPortfolioSection && (
            <div className="pt-1">
              <button
                onClick={() => setPortfolioOpen(!portfolioOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  location.pathname.startsWith("/portfolio")
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="portfolio-toggle"
              >
                <Images className="h-4 w-4" />
                <span className="font-medium">Portfolio</span>
                {portfolioOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {portfolioOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {portfolioItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-portfolio-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PLAN B: Management Section */}
          {showManagementSection && (
            <div className="pt-1">
              <button
                onClick={() => setManagementOpen(!managementOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  managementItems.some(i => location.pathname === i.path)
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="management-toggle"
              >
                <Users className="h-4 w-4" />
                <span className="font-medium">Management</span>
                {managementOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {managementOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {managementItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const Icon = item.icon;
                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setSidebarOpen(false)}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        className={cn(
                          "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                          isActive
                            ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                            : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* PLAN B: Admin Section */}
          {showAdminSection && (
            <div className="pt-1">
              <button
                onClick={() => setAdminOpen(!adminOpen)}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-md text-sm w-full transition-colors",
                  location.pathname.startsWith("/settings")
                    ? "text-orange-400"
                    : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                )}
                data-testid="admin-toggle"
              >
                <Settings className="h-4 w-4" />
                <span className="font-medium">Admin</span>
                {adminOpen ? (
                  <ChevronDown className="h-4 w-4 ml-auto" />
                ) : (
                  <ChevronRight className="h-4 w-4 ml-auto" />
                )}
              </button>
              {adminOpen && (
                <div className="ml-4 mt-1 space-y-0.5 border-l border-zinc-800 pl-3">
                  {ADMIN_GROUP_ORDER.flatMap((groupKey) => {
                    const groupItems = adminItems.filter((it) => (it.group || "system") === groupKey);
                    if (groupItems.length === 0) return [];
                    const groupHeader = (
                      <div
                        key={`group-${groupKey}`}
                        className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-wider font-semibold text-zinc-600"
                      >
                        {ADMIN_GROUP_LABELS[groupKey] || groupKey}
                      </div>
                    );
                    const rows = groupItems.map((item) => {
                      // Items with `?…` query params (e.g. Partner Feedback)
                      // need bare-pathname matching for the active state, since
                      // location.pathname doesn't include the search string.
                      const itemPathname = item.path.split("?")[0];
                      const isActive = location.pathname === itemPathname;
                      const Icon = item.icon;
                      const showErrorBadge = item.badgeKey === "errors" && errorBadge > 0;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setSidebarOpen(false)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={cn(
                            "flex items-center gap-3 px-3 py-1.5 rounded-md text-sm transition-colors",
                            isActive
                              ? "bg-orange-500/10 text-orange-500 border border-orange-500/20"
                              : "text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
                          )}
                        >
                          <Icon className="h-3.5 w-3.5" />
                          <span className="font-medium">{item.label}</span>
                          {showErrorBadge && (
                            <Badge className="ml-auto bg-red-500 text-white border-0 text-[10px] px-1.5 py-0">
                              {errorBadge}
                            </Badge>
                          )}
                        </Link>
                      );
                    });
                    return [groupHeader, ...rows];
                  })}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Bottom Actions — sits in the flex column below <nav>. Used to be
            absolute-positioned which caused the nav to scroll under it; now
            it's a real sibling, so nav can't lose its bottom items. */}
        <div className="flex-shrink-0 p-2 border-t border-zinc-800 bg-zinc-950">
          <div className="flex items-center gap-2">
            <Link
              to="/notifications"
              onClick={() => setSidebarOpen(false)}
              className="flex-1 flex items-center justify-center gap-2 px-2 py-2 rounded-md text-sm text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50 transition-colors"
              data-testid="nav-notifications"
            >
              <Bell className="h-4 w-4" />
              <span className="font-medium text-xs">Notifications</span>
              {unreadCount > 0 && (
                <Badge className="bg-orange-500 text-white border-0 text-[10px] px-1.5 py-0">{unreadCount}</Badge>
              )}
            </Link>
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 justify-center gap-2 px-2 py-2 text-zinc-400 hover:text-zinc-50 hover:bg-zinc-800/50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
              <span className="font-medium text-xs">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:ml-64 min-h-screen pt-16 lg:pt-0">
        {/* Desktop Header */}
        <header className="hidden lg:flex h-16 items-center justify-between px-8 border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-md sticky top-0 z-40">
          <div className="flex items-center gap-2 text-sm text-zinc-500">
            {navItems.map((item, index) => {
              if (location.pathname === item.path || 
                  (item.path !== "/dashboard" && location.pathname.startsWith(item.path))) {
                return (
                  <React.Fragment key={item.path}>
                    {index > 0 && <ChevronRight className="h-4 w-4" />}
                    <span className="text-zinc-50 font-medium">{item.label}</span>
                  </React.Fragment>
                );
              }
              return null;
            })}
          </div>

          <div className="flex items-center gap-4">
            <ThemeToggle />
            <Link to="/notifications" className="relative p-2 text-zinc-400 hover:text-zinc-50 transition-colors" data-testid="header-notifications-btn">
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 h-4 w-4 bg-orange-500 rounded-full text-xs flex items-center justify-center text-white">
                  {unreadCount}
                </span>
              )}
            </Link>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8 bg-zinc-800">
                    <AvatarFallback className="bg-zinc-800 text-zinc-300 text-sm">
                      {effectiveName?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-zinc-50">{effectiveName}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
                <DropdownMenuLabel className="text-zinc-400">My Account</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem className="text-zinc-300 focus:bg-zinc-800 focus:text-zinc-50">
                  <Badge variant="outline" className={cn("mr-2 capitalize", getRoleBadgeColor(effectiveRole))}>
                    {effectiveRole}
                  </Badge>
                  {user?.email}
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                <DropdownMenuItem
                  className="text-red-400 focus:bg-red-500/10 focus:text-red-400 cursor-pointer"
                  onClick={handleLogout}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6 lg:p-8">{children}</div>
      </main>
      
      {/* Currency Settings Modal */}
      <Dialog open={currencyModalOpen} onOpenChange={setCurrencyModalOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Settings className="h-5 w-5 text-orange-400" />
              Currency Settings
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg">
              <span className="text-sm text-zinc-400">Last update:</span>
              <span className="text-sm font-mono text-zinc-50">{rateDate || "—"}</span>
            </div>
            
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-zinc-400">USD to UAH</Label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">$1 =</span>
                  <Input
                    type="number"
                    step="0.0001"
                    value={manualUsd}
                    onChange={(e) => setManualUsd(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 flex-1"
                  />
                  <span className="text-zinc-500">₴</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-400">EUR to UAH</Label>
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">€1 =</span>
                  <Input
                    type="number"
                    step="0.0001"
                    value={manualEur}
                    onChange={(e) => setManualEur(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-zinc-50 flex-1"
                  />
                  <span className="text-zinc-500">₴</span>
                </div>
              </div>
            </div>
            
            <div className="p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
              <p className="text-xs text-blue-400">
                EUR/USD = {manualEur && manualUsd ? (parseFloat(manualEur) / parseFloat(manualUsd)).toFixed(4) : "—"}
              </p>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={handleFetchRates}
              disabled={fetchingRates}
              className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              <Download className="h-4 w-4 mr-2" />
              {fetchingRates ? "Fetching..." : "Fetch from NBU"}
            </Button>
            <Button
              onClick={handleSaveRates}
              disabled={savingRates}
              className="bg-orange-600 hover:bg-orange-700"
            >
              {savingRates ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SidebarLayout;
